/**
 * NewProviderCard
 *
 * Displays an unmatched provider mentioned in the call but not in the system.
 * Actions: Add Provider, Edit, Dismiss.
 */
import { AlertTriangle, Check, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';

import {
  type ReviewDecisionType,
  useSaveReviewDecisionMutation,
} from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Input } from '../../ui/input';
import { EvidenceList } from './EvidenceList';
import type { ReviewDecision, UnmatchedProviderItem } from './types';

interface NewProviderCardProps {
  item: UnmatchedProviderItem;
  matterId: string;
  checkInId: string;
  generationId: string;
  existingDecision?: ReviewDecision | null;
}

export const NewProviderCard = ({
  item,
  matterId,
  checkInId,
  generationId,
  existingDecision,
}: NewProviderCardProps) => {
  const [saveDecision, { isLoading }] = useSaveReviewDecisionMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  const isStale =
    existingDecision !== null &&
    existingDecision !== undefined &&
    existingDecision.generation_id !== generationId;

  const currentDecision = existingDecision?.decision ?? null;

  const handleDecision = (decision: ReviewDecisionType) => {
    saveDecision({
      matterId,
      checkInId,
      review_key: item.review_key,
      decision,
      generation_id: generationId,
      approved_payload: decision === 'added' ? { name: item.name, specialty: item.specialty ?? '', location: item.location ?? '' } : null,
      resolution: decision === 'added' ? { resolution_type: 'created_new', create_payload: { name: item.name, specialty: item.specialty ?? '', location: item.location ?? '' } } : undefined,
    });
  };

  const handleEditClick = () => {
    setEditValues({
      name: item.name,
      specialty: item.specialty ?? '',
      location: item.location ?? '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValues({});
  };

  const handleSaveEdit = () => {
    saveDecision({
      matterId,
      checkInId,
      review_key: item.review_key,
      decision: 'edited',
      generation_id: generationId,
      approved_payload: editValues,
      resolution: { resolution_type: 'created_new', create_payload: editValues },
    });
    setIsEditing(false);
    setEditValues({});
  };

  return (
    <Card className={isStale ? 'border-amber-500' : 'border-blue-200'}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-sm">{item.name}</CardTitle>
          <div className="flex items-center gap-1.5">
            <Badge variant="default" className="text-[10px]">
              New Provider
            </Badge>
            {item.specialty && (
              <Badge variant="outline" className="text-[10px]">
                {item.specialty}
              </Badge>
            )}
            <Badge
              variant={item.info_tier === 1 ? 'default' : 'secondary'}
              className="text-[10px]"
            >
              Tier {item.info_tier}
            </Badge>
            {isStale && (
              <Badge variant="outline" className="gap-1 text-[10px] text-amber-600">
                <AlertTriangle className="size-3" />
                Stale
              </Badge>
            )}
            {currentDecision && !isStale && (
              <Badge
                variant={currentDecision === 'added' ? 'default' : 'secondary'}
                className="text-[10px]"
              >
                {currentDecision}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 pt-0">
        {isEditing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="min-w-20 shrink-0 font-medium text-muted-foreground">
                Name:
              </span>
              <Input
                value={editValues.name ?? ''}
                onChange={(e) =>
                  setEditValues((prev) => ({ ...prev, name: e.target.value }))
                }
                className="h-7 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="min-w-20 shrink-0 font-medium text-muted-foreground">
                Specialty:
              </span>
              <Input
                value={editValues.specialty ?? ''}
                onChange={(e) =>
                  setEditValues((prev) => ({
                    ...prev,
                    specialty: e.target.value,
                  }))
                }
                className="h-7 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="min-w-20 shrink-0 font-medium text-muted-foreground">
                Location:
              </span>
              <Input
                value={editValues.location ?? ''}
                onChange={(e) =>
                  setEditValues((prev) => ({
                    ...prev,
                    location: e.target.value,
                  }))
                }
                className="h-7 text-sm"
              />
            </div>
          </div>
        ) : (
          <>
            {item.location && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Location:</span> {item.location}
              </p>
            )}
            {item.treatment_status && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Status:</span> {item.treatment_status}
              </p>
            )}
            {item.notes && (
              <p className="text-xs text-muted-foreground">{item.notes}</p>
            )}
          </>
        )}

        {/* Evidence excerpts */}
        <EvidenceList evidence={item.evidence} />
      </CardContent>

      <CardFooter className="gap-2">
        {isEditing ? (
          <>
            <Button
              size="sm"
              disabled={isLoading}
              onClick={handleSaveEdit}
            >
              <Check className="mr-1 size-3" />
              Save Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isLoading}
              onClick={handleCancelEdit}
            >
              <X className="mr-1 size-3" />
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant={currentDecision === 'added' && !isStale ? 'default' : 'outline'}
              disabled={isLoading}
              onClick={() => handleDecision('added')}
            >
              <Plus className="mr-1 size-3" />
              Add Provider
            </Button>
            <Button
              size="sm"
              variant={currentDecision === 'edited' && !isStale ? 'default' : 'outline'}
              disabled={isLoading}
              onClick={handleEditClick}
            >
              <Pencil className="mr-1 size-3" />
              Edit
            </Button>
            <Button
              size="sm"
              variant={currentDecision === 'dismissed' && !isStale ? 'destructive' : 'outline'}
              disabled={isLoading}
              onClick={() => handleDecision('dismissed')}
            >
              <X className="mr-1 size-3" />
              Dismiss
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};
